import { Inject, Injectable, Optional } from '@angular/core';
import {
  HttpClient, HttpHeaders,
  HttpResponse, HttpEvent, HttpParams
} from '@angular/common/http';

import { Observable } from 'rxjs';

import { ErrorModel } from '../model/errorModel';
import { OrderModel } from '../model/orderModel';

import { BASE_PATH, COLLECTION_FORMATS } from '../variables';
import { Configuration } from '../configuration';
import {
  CodeRequestModel,
  OrderConfirmModel,
  OrderInvoiceModel,
  OrderOnRentDamageModel,
  ResponseModel
} from '@core/api';
import { PaginationModel } from '@core/api/model/paginationModel';
import { OrderExtraModel } from '@core/api/model/orderExtraModel';
import {OrderReportModel} from "@core/api/model/orderReportModel";


@Injectable()
export class OrderService {

  protected basePath = 'https://localhost/v1';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor (protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  private canConsumeForm (consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }


  /**
   *
   * Get a order list
   * @param body
   */
  public orderGet (body: HttpParams = null): Observable<ResponseModel<OrderModel[], PaginationModel>> {

    return this.httpClient.get<ResponseModel<OrderModel[]>>(`${this.basePath}/order?include=user,trailer,transport,gasStation,returnGasStation,actions,network`,
      {
        params: body
      }
    );
  }

  /**
   *
   * Get a orders reports list
   * @param body
   */
  public orderReportGet (body: HttpParams = null): Observable<ResponseModel<OrderReportModel[], PaginationModel>> {

    return this.httpClient.get<ResponseModel<OrderReportModel[]>>(`${this.basePath}/reports/orders`,
        {
          params: body
        }
    );
  }

  public downloadReport(body: HttpParams = null): any {
    return this.httpClient.get(`${this.basePath}/reports/orders/export`,
        {
          responseType: 'blob',
          params: body
        }
    );
  }

  public downloadTransferAct(id: number): any {
    return this.httpClient.get(`${this.basePath}/order/${encodeURIComponent(String(id))}/transfer-act`,
        {
          responseType: 'blob'
        }
    );
  }

  public downloadReturnAct(id: number): any {
    return this.httpClient.get(`${this.basePath}/order/${encodeURIComponent(String(id))}/return-act`,
        {
          responseType: 'blob'
        }
    );
  }

  public getOrderConfirmationCode(id: number): Observable<any> {
    return this.httpClient.get(`${this.basePath}/order/${encodeURIComponent(String(id))}/confirmations`);
  }

  /**
   *
   * Get a order list
   * @param id
   */
  public orderIdGet (id: number): Observable<ResponseModel<OrderModel, OrderExtraModel>> {
    return this.httpClient.get<ResponseModel<OrderModel, OrderExtraModel>>(`${this.basePath}/order/${ encodeURIComponent(String(id)) }?include=user,trailer,transport,gasStation,returnGasStation,returnDamages,actions`);
  }

  /**
   *
   * Creates a new order
   * @param body
   */
  public orderPost (body: OrderModel): Observable<ResponseModel<OrderModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling orderPost.');
    }

    return this.httpClient.post<ResponseModel<OrderModel>>(`${this.basePath}/order`,
      body
    );
  }

  /**
   *
   * Change a order status
   * @param id
   * @param body
   */
  public orderIdPut (id: number, body: HttpParams): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdPut.');
    }

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling orderIdPut.');
    }

    return this.httpClient.put<ResponseModel<null>>(`${this.basePath}/order/${ encodeURIComponent(String(id)) }/status`,
      body
    );
  }


  /**
   *
   * Delete specified on rent damage
   * @param id Order id
   * @param damage_id Order on rent damage id
   */
  public orderIdOnRentDamageDamageIdDelete(id: number, damage_id: number): Observable<ResponseModel<null>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdOnRentDamageDamageIdDelete.');
    }

    if (damage_id === null || damage_id === undefined) {
      throw new Error('Required parameter damage_id was null or undefined when calling orderIdOnRentDamageDamageIdDelete.');
    }

    return this.httpClient.delete<ResponseModel<null>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/on-rent-damage/${encodeURIComponent(String(damage_id))}` );
  }

  /**
   *
   * Get list of order on rent trailer damages
   * @param id Order id
   */
  public orderIdOnRentDamageGet(id: number,): Observable<ResponseModel<OrderOnRentDamageModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdOnRentDamageGet.');
    }

    return this.httpClient.get<ResponseModel<OrderOnRentDamageModel[]>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/on-rent-damage`);
  }

  /**
   *
   * Creates a new on rent damage
   * @param body
   * @param id Order id
   */
  public orderIdOnRentDamagePost(body: OrderOnRentDamageModel, id: number): Observable<ResponseModel<OrderOnRentDamageModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling orderIdOnRentDamagePost.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdOnRentDamagePost.');
    }

    return this.httpClient.post<ResponseModel<OrderOnRentDamageModel>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/on-rent-damage`,
      body
    );
  }

  /**
   *
   * Get list of order invoices
   * @param id Order id
   */
  public orderIdInvoiceGet(id: number): Observable<ResponseModel<OrderInvoiceModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdInvoiceGet.');
    }


    return this.httpClient.get<ResponseModel<OrderInvoiceModel[]>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/invoice`);
  }

  /**
   *
   * Creates a new invoice
   * @param body
   * @param id Order id
   */
  public orderIdInvoicePost(body: OrderInvoiceModel, id: number): Observable<ResponseModel<OrderInvoiceModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling orderIdInvoicePost.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdInvoicePost.');
    }

    return this.httpClient.post<ResponseModel<OrderInvoiceModel>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/invoice`,
       body
    );
  }

  /**
   *
   */
  public getBasePath(): string {
    return this.basePath;
  }

  /**
   *
   * Creates a new invoice
   * @param body
   * @param id Order id
   */
  orderIdConfirmPost(body: OrderConfirmModel, id: number): Observable<ResponseModel<CodeRequestModel>> {
    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling orderIdConfirmPost.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdConfirmPost.');
    }

    return this.httpClient.post<ResponseModel<CodeRequestModel>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/confirm`,
       body
    );
  }

  orderIdExpiredTimePut(id: number): Observable<ResponseModel<OrderModel>> {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdConfirmPost.');
    }

    return this.httpClient.put<ResponseModel<OrderModel>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/update-expired-time`, {});
  }

  orderIdAdapterPassedPut(id: number, adapter: boolean): Observable<ResponseModel<OrderModel>> {
    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling orderIdConfirmPost.');
    }

    return this.httpClient.put<ResponseModel<OrderModel>>(`${this.basePath}/order/${encodeURIComponent(String(id))}/update-adapter-passed`, { adapter_passed: adapter });
  }
}
